import { useMemo, useState } from 'react';
import { NJButton, NJLink, NJText } from '@engie-group/fluid-design-system-react';
import type { ProjectSummary } from './types';

interface PlanRow {
  id: number;
  name: string;
  buildings: number;
  consumption: number;
  length: number;
  updatedAt: string;
  updatedISO: string;
  updatedTime: number;
}

const PLAN_NAMES = [
  'Scénario de référence', 'Extension vers les écoles', 'Boucle du centre-ville',
  'Raccordement des logements', 'Variante géothermie', 'Desserte des équipements publics',
  'Extension Val de Bièvre', 'Variante biomasse',
];

function getDevelopmentPlanRows(project: ProjectSummary): PlanRow[] {
  return Array.from({ length: project.planCount }, (_, index) => {
    const buildings = Math.max(1, Math.round(project.buildingCount * Math.min(0.95, 0.25 + index * 0.12)));
    const [datePart, timePart] = project.updatedAt.split(' ');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day - index * 2, hours, minutes));
    return {
      id: index + 1, name: PLAN_NAMES[index % PLAN_NAMES.length], buildings, consumption: buildings * 0.085, length: buildings * 0.012,
      updatedAt: date.toLocaleString('fr-FR', { timeZone: 'UTC', dateStyle: 'short', timeStyle: 'short' }),
      updatedISO: date.toISOString(), updatedTime: date.getTime(),
    };
  }).sort((a, b) => b.updatedTime - a.updatedTime);
}

function formatPlanMetric(value: number): string {
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
}

export function DevelopmentPlansTable({ project, flash }: {
  project: ProjectSummary;
  flash: (message: string) => void;
}) {
  const [allPlans, setAllPlans] = useState(false);
  const rows = useMemo(() => getDevelopmentPlanRows(project), [project.buildingCount, project.planCount, project.updatedAt]);

  return (
    <div className="pcv3-wf-plans">
      <NJText as="strong">{project.planCount} plan{project.planCount > 1 ? 's' : ''} de développement</NJText>
      {rows.length ? (
        <div className="pcv3-plans-table" role="region" aria-label={`Plans de développement · ${project.name}`} tabIndex={0}>
          <table className="nj-table nj-table--striped nj-table--hover pcv3-data-table" id={`plans-table-${project.id}`} aria-label={`Plans de développement · ${project.name}`}>
            <colgroup>
              <col className="pcv3-plan-name-col" />
              <col className="pcv3-plan-buildings-col" /><col className="pcv3-plan-consumption-col" /><col className="pcv3-plan-length-col" />
              <col className="pcv3-plan-date-col" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col"><NJText as="span" scale="xs">Plan de développement</NJText></th>
                <th scope="col"><div className="pcv3-table-number"><NJText as="span" scale="xs">Bâtiments</NJText></div></th>
                <th scope="col"><div className="pcv3-table-number"><NJText as="span" scale="xs" className="pcv3-table-nowrap">Conso totale<br />GWh/an</NJText></div></th>
                <th scope="col"><div className="pcv3-table-number"><NJText as="span" scale="xs">Tracé<br />km</NJText></div></th>
                <th scope="col" aria-sort="descending"><NJText as="span" scale="xs">Dernière modification ↓</NJText></th>
              </tr>
            </thead>
            <tbody>
              {(allPlans ? rows : rows.slice(0, 2)).map((row) => (
                <tr key={row.name}>
                  <th scope="row">
                    <NJLink size="sm" variant="contextual" href={`#plan-${project.id}-${row.id}`}
                      title="Ouvrir le plan de développement · page à venir"
                      onClick={(event) => {
                        event.preventDefault();
                        flash(`${row.name} · ${project.name} · page du plan de développement à venir`);
                      }}>{row.name}</NJLink>
                  </th>
                  <td><div className="pcv3-table-number"><NJText as="span" scale="sm">{formatPlanMetric(row.buildings)}</NJText></div></td>
                  <td><div className="pcv3-table-number"><NJText as="span" scale="sm">{formatPlanMetric(row.consumption)}</NJText></div></td>
                  <td><div className="pcv3-table-number"><NJText as="span" scale="sm">{formatPlanMetric(row.length)}</NJText></div></td>
                  <td><NJText as="time" dateTime={row.updatedISO} scale="sm">{row.updatedAt}</NJText></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <NJText scale="sm">Aucun plan de développement.</NJText>}
      {rows.length > 2 && (
        <NJButton variant="secondary" scale="sm"
          label={allPlans ? 'Voir moins' : `Voir plus (+${rows.length - 2} plans)`}
          aria-expanded={allPlans} aria-controls={`plans-table-${project.id}`}
          onClick={() => setAllPlans((value) => !value)} />
      )}
    </div>
  );
}
